// Drawer - right-side slide-over used for add/edit forms across inventory pages.
// Click backdrop or Escape to close. Body scroll locked while open (counted, G360_K641).
//
// G360_K680 - keyboard and screen reader. Same props as before; every drawer gets this:
//   - the dialog is named by its title (aria-labelledby);
//   - opening moves focus to the title, and Tab and Shift+Tab stay inside the drawer while it is open;
//   - closing puts focus back on what opened it;
//   - a closed drawer is inert: nothing in it can be reached with Tab (before, its Close button and
//     footer stayed in the tab order, off screen). It is set as the drawer renders, so a field in the
//     drawer that takes focus when it opens still gets it;
//   - when one drawer closes and another opens in the same click, closing the second puts focus back
//     where the first was opened from;
//   - no slide when the system asks for reduced motion; on a screen narrower than the drawer it is
//     the full width of the screen (before: 92% of it).

import { useEffect, useId, useRef, version as reactVersion } from 'react';
import { X } from 'lucide-react';

// G360_K641 - ONE COUNTED SCROLL LOCK FOR EVERY DRAWER AND THE PALETTE.
// Each drawer used to save body.style.overflow on open and put it back on
// close. With two drawers open at once (a list drawer over a detail drawer,
// which the console does everywhere) and closed in a different order, the
// last one to close put back "hidden" - the page stayed locked and could not
// scroll until a reload. Now every lock is counted: the page is locked while
// any drawer is open and always unlocked when the last one closes. The count
// lives on window so a second copy of this module still shares it.
function lockState() {
  if (typeof window === 'undefined') return { n: 0, prev: '' };
  if (!window.__g360ScrollLock) window.__g360ScrollLock = { n: 0, prev: '' };
  return window.__g360ScrollLock;
}
export function lockBodyScroll() {
  const st = lockState();
  if (typeof document === 'undefined') return () => {};
  if (st.n === 0) {
    const cur = document.body.style.overflow;
    st.prev = cur === 'hidden' ? '' : cur;
    document.body.style.overflow = 'hidden';
  }
  st.n += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    st.n = Math.max(0, st.n - 1);
    if (st.n === 0) document.body.style.overflow = st.prev || '';
  };
}

const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/* React 19 knows inert as a boolean; React 18 passes an unknown attribute through only as a string,
   so there it is inert="" when closed and absent when open. */
const REACT19 = parseInt(String(reactVersion || '18'), 10) >= 19;
function inertProps(closed) { return REACT19 ? { inert: closed } : (closed ? { inert: '' } : {}); }

/* Where focus goes back to: the opener, or - when the opener sat in a drawer that has closed too -
   whatever opened that drawer. */
function focusBack(el) {
  let cur = el;
  for (let i = 0; cur && i < 6; i++) {
    const host = cur.closest ? cur.closest('[data-g360-drawer]') : null;
    if (!host || host.getAttribute('data-g360-drawer') === 'open') break;
    cur = host.__g360Opener || null;
  }
  if (cur && cur.isConnected && typeof cur.focus === 'function') { try { cur.focus({ preventScroll: true }); } catch (e) { /* gone */ } }
}

function prefersReducedMotion() {
  try { return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
}

export default function Drawer({ open, title, onClose, children, footer, width = 460 }) {
  // G360_K641 - the lock follows open only; a new onClose each render no
  // longer releases and re-takes it.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const headRef = useRef(null);
  const openerRef = useRef(null);
  const capturedRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  // What had focus when the drawer opened, read as it renders open - before any field inside it can
  // take focus - so closing can put focus back there.
  if (open && !capturedRef.current && typeof document !== 'undefined') {
    capturedRef.current = true;
    const a = document.activeElement;
    openerRef.current = a && a !== document.body && !(panelRef.current && panelRef.current.contains(a)) ? a : null;
  }
  const titleId = 'g360-drawer-' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && closeRef.current) closeRef.current(); };
    document.addEventListener('keydown', onKey);
    const unlock = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, [open]);

  // G360_K680 - focus in on open (unless something inside already took it), back on close.
  useEffect(() => {
    if (!open) return undefined;
    if (rootRef.current) rootRef.current.__g360Opener = openerRef.current;
    const t = setTimeout(() => {
      const panel = panelRef.current;
      if (panel && !panel.contains(document.activeElement) && headRef.current) headRef.current.focus({ preventScroll: true });
    }, 0);
    return () => {
      clearTimeout(t);
      // Still open and still on the page: the effect is only being run again (React's strict mode in
      // development), so keep what opened the drawer.
      if (openRef.current && rootRef.current && rootRef.current.isConnected) return;
      const back = openerRef.current;
      openerRef.current = null;
      capturedRef.current = false;
      if (back) setTimeout(() => focusBack(back), 0);
    };
  }, [open]);

  function trapTab(e) {
    // A drawer inside another drawer handles Tab first; the outer one then leaves it alone.
    if (e.key !== 'Tab' || e.defaultPrevented || !panelRef.current) return;
    const items = Array.prototype.filter.call(panelRef.current.querySelectorAll(FOCUSABLE),
      (el) => (el.offsetParent !== null || el === document.activeElement) && !el.closest('[inert], [aria-hidden="true"]'));
    if (!items.length) { e.preventDefault(); if (headRef.current) headRef.current.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const cur = document.activeElement;
    const atPanel = cur === panelRef.current || cur === headRef.current;
    if (e.shiftKey && (cur === first || atPanel || !panelRef.current.contains(cur))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (cur === last || cur === panelRef.current || !panelRef.current.contains(cur))) { e.preventDefault(); first.focus(); }
  }

  const still = prefersReducedMotion();
  const w = typeof width === 'number' ? 'min(' + width + 'px, 100vw)' : width;

  return (
    <div
      ref={rootRef}
      aria-hidden={!open}
      {...inertProps(!open)}
      data-g360-drawer={open ? 'open' : 'closed'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15,23,42,0.35)',
          opacity: open ? 1 : 0,
          transition: still ? 'none' : 'opacity 0.2s',
        }}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={trapTab}
        style={{
          outline: 'none',
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: w,
          maxWidth: '100vw',
          background: 'var(--g-surface)',
          borderLeft: '1px solid var(--g-divider)',
          boxShadow: 'var(--g-e3)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: still ? 'none' : 'transform 0.24s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 22px',
            borderBottom: '1px solid var(--g-divider)',
          }}
        >
          <h3 id={titleId} ref={headRef} tabIndex={-1} style={{ margin: 0, fontFamily: 'var(--g-font-display)', fontSize: 17, fontWeight: 500, outline: 'none' }}>
            {title}
          </h3>
          <button className="ap-ic" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '16px 22px',
              borderTop: '1px solid var(--g-divider)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
